import { useCallback, useEffect, useState } from 'react';
import {
  AllTemplatesForBuilderDocument,
  GetVersionsDocument,
  TemplateTypeEnum,
  useAddAssessmentDetailsFieldsMutation,
  useAddMiServicesFieldsMutation,
  useAddMiServicesOverhaulFieldsForAssemblyTemplateMutation,
  useAllTemplatesForBuilderQuery,
  useEvaluateAllAssembliesForStructureMutation,
} from '@/__generated__/graphql';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { useSnackBarMessage } from './utils';
import DownloadIcon from '@mui/icons-material/Download';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AddTemplateFieldsToStructureDialog } from './AddTemplateFieldsToStructureDialog';
import { StructuresList } from './StructuresList';
import { TemplateForm } from './TemplateForm';
import {
  assemblyFields,
  assemblyTemplate,
  selectedTemplateField as selectedAssemblyField,
  selectedTemplateId,
} from './state';
import { GenericDialog } from '../shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { Calculate } from '@mui/icons-material';
import { PageLoader } from '../shared/PageLoader';
import { useAtom, useSetAtom } from 'jotai';

type AssemblyTemplateProps = {
  structureId: string;
  structureName: string;
};

export const AssemblyTemplate = ({ structureId, structureName }: AssemblyTemplateProps) => {
  const setAllTemplateFields = useSetAtom(assemblyFields);
  const setselectedTemplateField = useSetAtom(selectedAssemblyField);
  const [templateId, setTemplateId] = useAtom(selectedTemplateId);
  const setSelectedAssemblyTemplate = useSetAtom(assemblyTemplate);
  const [emptyForm, setEmptyForm] = useState<boolean>(false);
  const [showStructuresList, setShowStructuresList] = useState<boolean>(false);
  const [selectedStructureId, setSelectedStructureId] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMiServicesConfirmationDialogOpen, setIsMiServicesConfirmationDialogOpen] =
    useState<boolean>(false);
  const [
    isMiServicesOverhaulConfirmationDialogOpen,
    setIsMiServicesOverhaulConfirmationDialogOpen,
  ] = useState<boolean>(false);
  const [isMIRecalucationDialogOpen, setIsMIRecalucationDialogOpen] = useState<boolean>(false);
  const [
    isAssessmentDetailsFieldsConfirmationDialogOpen,
    setAssessmentDetailsFieldsConfirmationDialogOpen,
  ] = useState<boolean>(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { data, refetch } = useAllTemplatesForBuilderQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data?.allTemplates && data.allTemplates.length > 0) {
      setSelectedAssemblyTemplate(data.allTemplates.at(0));
      setTemplateId(data.allTemplates.at(0)?.id);
      setAllTemplateFields(data.allTemplates.at(0)?.fields);
      setselectedTemplateField(data.allTemplates.at(0)?.fields.at(0));
    }
  }, [
    data?.allTemplates,
    setSelectedAssemblyTemplate,
    setAllTemplateFields,
    setTemplateId,
    setselectedTemplateField,
  ]);

  const handlePlatformClick = useCallback(
    (id: string) => {
      if (id === structureId) {
        showSnackBar(`You have already selected ${structureName}`, 'info');
      } else {
        setSelectedStructureId(id);
        setIsOpen(true);
      }
    },
    [showSnackBar, structureId, structureName]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const [addMiServicesFields] = useAddMiServicesFieldsMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
  });

  const handleAddMiServicesFields = useCallback(() => {
    addMiServicesFields({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: (response) => {
        if (response?.addMiServicesFields) {
          showSnackBar('MI Services Fields added successfully', 'success');
          setIsMiServicesConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsMiServicesConfirmationDialogOpen(false);
      },
    });
  }, [addMiServicesFields, showSnackBar, structureId, templateId]);

  const [evaluateAllAssemblies, { loading: updatingAssemblies }] =
    useEvaluateAllAssembliesForStructureMutation({
      refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
    });

  const handleEvaluation = useCallback(() => {
    evaluateAllAssemblies({
      variables: {
        structureId,
      },
      onCompleted: (response) => {
        if (response?.evaluateAllAssembliesForStructure) {
          showSnackBar('MI logic was successfully recalculated for this deployment', 'success');
          setIsMIRecalucationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsMIRecalucationDialogOpen(false);
      },
    });
  }, [evaluateAllAssemblies, showSnackBar, structureId]);

  const [addMiServicesOverhaulFields] = useAddMiServicesOverhaulFieldsForAssemblyTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
  });

  const handleAddMiServicesOverhaulFields = useCallback(() => {
    addMiServicesOverhaulFields({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: (response) => {
        if (response?.addMiServicesOverhaulFields) {
          showSnackBar('MI Services Overhaul Fields added successfully', 'success');
          setIsMiServicesOverhaulConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsMiServicesOverhaulConfirmationDialogOpen(false);
      },
    });
  }, [addMiServicesOverhaulFields, showSnackBar, structureId, templateId]);

  const [addAssessmentDetailsFields] = useAddAssessmentDetailsFieldsMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
  });

  const handleAddAssessmentDetailsFields = useCallback(() => {
    addAssessmentDetailsFields({
      variables: {
        input: {
          structureId,
          type: TemplateTypeEnum.Assembly,
          templateId: templateId ?? '',
        },
      },
      onCompleted: (response) => {
        if (response?.addAssessmentDetailsFields) {
          showSnackBar('Assessment Details Fields added successfully', 'success');
          setAssessmentDetailsFieldsConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setAssessmentDetailsFieldsConfirmationDialogOpen(false);
      },
    });
  }, [addAssessmentDetailsFields, showSnackBar, structureId, templateId]);

  const downloadJson = useCallback(() => {
    refetch().then(({ data: allTemplatesData }) => {
      const allTemplates = allTemplatesData?.allTemplates;
      const jsonData = JSON.stringify(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        allTemplates?.map(({ __typename, ...template }) => template),
        undefined,
        2
      );
      const jsonBlob = new Blob([jsonData], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(jsonBlob);
      const fileName = `${structureName}-assembly-templates-${Date.now().toString()}.json`;
      link.download = fileName;
      document.body.append(link);
      link.click();
      link.remove();
    });
  }, [refetch, structureName]);

  return (
    <Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          pt: 4,
          alignItems: 'center',
        }}
      >
        {showStructuresList && (
          <Typography variant="h6">Select a platform to copy fields from</Typography>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          {showStructuresList && (
            <Button
              startIcon={<VisibilityIcon />}
              variant="contained"
              onClick={() => {
                setShowStructuresList(false);
              }}
              sx={containedButtonStyle}
            >
              View Template Fields
            </Button>
          )}
          <Button
            startIcon={<Calculate />}
            variant="contained"
            sx={containedButtonStyle}
            onClick={() => setIsMIRecalucationDialogOpen(true)}
          >
            MI Recalculation
          </Button>
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            sx={containedButtonStyle}
            onClick={() => setIsMiServicesConfirmationDialogOpen(true)}
          >
            Add MI Services Fields
          </Button>
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            sx={containedButtonStyle}
            onClick={() => setIsMiServicesOverhaulConfirmationDialogOpen(true)}
          >
            Add MI Services Overhaul Fields
          </Button>
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            sx={containedButtonStyle}
            onClick={() => setAssessmentDetailsFieldsConfirmationDialogOpen(true)}
          >
            Add Assessment Details Fields
          </Button>
          <Button
            startIcon={<FileCopyIcon />}
            variant="contained"
            sx={containedButtonStyle}
            onClick={() => {
              setShowStructuresList(true);
            }}
          >
            Copy Fields From Another Platform
          </Button>
          {!showStructuresList && (
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={() => downloadJson()}
              sx={containedButtonStyle}
            >
              Download JSON
            </Button>
          )}
        </Stack>
      </Stack>

      {!showStructuresList && (
        <TemplateForm
          handleEmptyForm={(value) => setEmptyForm(value)}
          emptyForm={emptyForm}
          currentTemplateType={TemplateTypeEnum.Assembly.toString()}
          structureId={structureId}
        />
      )}
      {showStructuresList && (
        <Box>
          <StructuresList onPlatformClick={handlePlatformClick} drawerOpen />
        </Box>
      )}
      {isOpen && (
        <AddTemplateFieldsToStructureDialog
          isOpen={isOpen}
          structureId={selectedStructureId}
          onClose={handleClose}
          structureName={structureName}
          currentStructureId={structureId}
        />
      )}
      {isMiServicesConfirmationDialogOpen && (
        <GenericDialog
          open={isMiServicesConfirmationDialogOpen}
          onClose={() => setIsMiServicesConfirmationDialogOpen(false)}
          title="Add MI Services Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add MI Services Fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsMiServicesConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddMiServicesFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isMiServicesOverhaulConfirmationDialogOpen && (
        <GenericDialog
          open={isMiServicesOverhaulConfirmationDialogOpen}
          onClose={() => setIsMiServicesOverhaulConfirmationDialogOpen(false)}
          title="Add MI Services Overhaul Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add MI Services Overhaul Fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsMiServicesOverhaulConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddMiServicesOverhaulFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isMIRecalucationDialogOpen && (
        <GenericDialog
          open={isMIRecalucationDialogOpen}
          onClose={() => setIsMIRecalucationDialogOpen(false)}
          title="Re-run the MI Evaluation Process"
          content={
            <>
              <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
                {updatingAssemblies
                  ? 'Calculations are now being performed for all assemblies using the updated logic. This process may take some time.'
                  : 'This action will trigger a full recalculation of the MI logic for this deployment. Are you sure you want to continue?'}
              </Typography>
              {updatingAssemblies && (
                <Stack marginTop="3.2rem">
                  <PageLoader variant="sm" flashing hideProgress />
                </Stack>
              )}
            </>
          }
          actions={
            <>
              {!updatingAssemblies && (
                <>
                  <Button
                    variant="text"
                    onClick={() => setIsMIRecalucationDialogOpen(false)}
                    sx={textButtonStyle}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleEvaluation()}
                    sx={containedButtonStyle}
                  >
                    Confirm
                  </Button>
                </>
              )}
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isAssessmentDetailsFieldsConfirmationDialogOpen && (
        <GenericDialog
          open={isAssessmentDetailsFieldsConfirmationDialogOpen}
          onClose={() => setAssessmentDetailsFieldsConfirmationDialogOpen(false)}
          title="Add Assessment Details Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add assessment details fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setAssessmentDetailsFieldsConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddAssessmentDetailsFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </Stack>
  );
};
